// Helper: map UI type/status to API enum ints
export const TEMPLATE_TYPE_MAP: Record<string, number> = {
    Welcome: 0,
  Booking: 1,
  Notification: 2,
  Marketing: 3,
  Other: 4,
};
const STATUS_MAP: Record<string, number> = {
  Draft: 0,
  Active: 1,
  Inactive: 2,
};
function toApiTemplatePayload(t: Partial<EmailTemplate>) {
  return {
    TemplateName: t.name,
    Subject: t.subject,
    BodyHtml: t.htmlContent,
    TemplateType: typeof t.type === 'number' ? t.type : TEMPLATE_TYPE_MAP[t.type || 'Other'] ?? 3,
    Status: typeof t.status === 'number' ? t.status : STATUS_MAP[t.status || 'Active'] ?? 1,
    Actor: (typeof window !== 'undefined' ? (localStorage.getItem('user') || 'system') : 'system'),
  };
}
import api from '../api/axiosClient';

// Basic types (loosely typed for pragmatism)
export interface EmailTemplate {
  id?: number;
  name: string; // UI name (maps to TemplateName)
  subject?: string; // maps to Subject
  type: string; // e.g. BookingConfirmation, maps to TemplateType
  status?: string; // Active / Inactive (maps to enum int in API)
  lastUsedAt?: string;
  htmlContent: string; // maps to BodyHtml
  variables?: string[]; // available merge vars like {{ClientName}}
}

export interface EmailSender {
  id?: number;
  emailAddress: string;
  providerType: number | string; // numeric enum (1 SMTP, 2 SendGrid, 3 SES) or display string
  description?: string;
  isActive: boolean;
}

export interface EmailMapping {
  id?: string;
  emailSenderId: string;
  emailTemplateId: string;
  notes?: string;
  createdAt?: string;
  emailSender?: EmailSender;
  emailTemplate?: EmailTemplate;
}

// Backend uses hyphenated resources per API Requirement: /api/email-templates, /api/email-senders, /api/email-integrations
const TEMPLATES_BASE = '/email-templates';
const SENDERS_BASE = '/email-senders';
const INTEGRATIONS_BASE = '/email-integrations';

// Helper: get current actor/user for audit fields
const getActor = () => (typeof window !== 'undefined' ? (localStorage.getItem('user') || 'system') : 'system');

// No longer needed: toApiTemplatePayload. Use direct mapping in createTemplate/updateTemplate.

// Helper: map API response to UI template
const fromApiTemplate = (r: any): EmailTemplate => ({
  id: r.id,
  name: r.templateName || r.name,
  subject: r.subject,
  type: r.templateType ?? r.type,
  status: typeof r.status === 'number' ? String(r.status) : r.status,
  lastUsedAt: r.lastUsedAt,
  htmlContent: r.bodyHtml ?? r.htmlContent ?? '',
  variables: r.variables || [],
});

// Templates
export const getTemplates = async (query?: { status?: number|string; type?: number|string; search?: string }) => {
  const params = new URLSearchParams();
  if (query?.status !== undefined && query?.status !== '') params.append('status', String(query.status));
  if (query?.type !== undefined && query?.type !== '') params.append('type', String(query.type));
  if (query?.search) params.append('search', query.search);
  const url = `${TEMPLATES_BASE}${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await api.get(url);
  const data = res.data;
  return Array.isArray(data) ? data.map(fromApiTemplate) : (Array.isArray(data?.templates) ? data.templates.map(fromApiTemplate) : data);
};

export const getTemplateById = async (id: string | number) => {
  const res = await api.get(`${TEMPLATES_BASE}/${id}`);
  return fromApiTemplate(res.data);
};

export const createTemplate = async (data: Partial<EmailTemplate>) => {
  const payload = toApiTemplatePayload(data);
  const res = await api.post(TEMPLATES_BASE, payload);
  return fromApiTemplate(res.data);
};

export const updateTemplate = async (id: number | string, data: Partial<EmailTemplate>) => {
  const payload = toApiTemplatePayload(data);
  const res = await api.put(`${TEMPLATES_BASE}/${id}`, payload);
  return fromApiTemplate(res.data);
};

export const deleteTemplate = async (id: number | string) => {
  const actor = encodeURIComponent(getActor());
  const res = await api.delete(`${TEMPLATES_BASE}/${id}?actor=${actor}`);
  return res.data;
};

export const previewTemplate = async (id: number | string, payload?: Record<string, any>) => {
  // Backend returns { subject, body }
  const res = await api.post(`${TEMPLATES_BASE}/${id}/preview`, payload || {});
  return res.data as { subject: string; body: string };
};

export const sendTestTemplate = async (
  id: number | string,
  senderId: string,
  recipientEmail: string,
  payload?: Record<string, any>
) => {
  const body = { SenderId: senderId, RecipientEmail: recipientEmail, Payload: payload || {} };
  const res = await api.post(`${TEMPLATES_BASE}/${id}/send-test`, body);
  return res.data;
};

// Senders
export const getSenders = async () => {
  const res = await api.get(SENDERS_BASE);
  return res.data;
};

export const createSender = async (data: Partial<EmailSender>) => {
  const payload = toApiSenderPayload(data);
  const res = await api.post(SENDERS_BASE, payload);
  return res.data;
};

export const updateSender = async (id: string, data: Partial<EmailSender>) => {
  const payload = toApiSenderPayload(data);
  const res = await api.put(`${SENDERS_BASE}/${id}`, payload);
  return res.data;
};

export const deleteSender = async (id: string) => {
  const res = await api.delete(`${SENDERS_BASE}/${id}`);
  return res.data;
};

// Integrations (Mappings)
export const getMappings = async () => {
  const res = await api.get(INTEGRATIONS_BASE);
  // API returns array of mappings with nested sender/template
  return res.data;
};

export const createMapping = async (data: { emailSenderId: string; emailTemplateId: string; notes?: string }) => {
  // API expects EmailSenderId, EmailTemplateId, Notes
  const payload = {
    EmailSenderId: data.emailSenderId,
    EmailTemplateId: data.emailTemplateId,
    Notes: data.notes || '',
  };
  const res = await api.post(INTEGRATIONS_BASE, payload);
  return res.data;
};

export const deleteMapping = async (id: string) => {
  const res = await api.delete(`${INTEGRATIONS_BASE}/${id}`);
  return res.data;
};

export default {
  getTemplates,
  getTemplateById,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  previewTemplate,
  sendTestTemplate,
  getSenders,
  createSender,
  updateSender,
  deleteSender,
  getMappings,
  createMapping,
  deleteMapping,
};

// --- Internal mapping helpers for sender payload ---
function toApiSenderPayload(s: Partial<EmailSender>) {
  // New numeric mapping: 1 SMTP, 2 SendGrid, 3 SES
  const providerMap: Record<string, number> = {
    SMTP: 1,
    SendGrid: 2,
    SES: 3,
  };
  let providerTypeValue: number | undefined;
  if (typeof s.providerType === 'string') providerTypeValue = providerMap[s.providerType] ?? undefined;
  else if (typeof s.providerType === 'number') providerTypeValue = s.providerType;

  return {
    EmailAddress: s.emailAddress,
    Description: s.description,
    ProviderType: providerTypeValue,
    IsActive: s.isActive !== undefined ? s.isActive : true,
  };
}
